import type { CandlestickData } from 'lightweight-charts';

import type { TimeValue } from '../../models/contracts';
import type {
  CandleHeatmapRangeOptions,
  CandleHeatmapShader,
  CandleHeatmapOptions,
  CandleHeatmapPartialOptions,
} from '../../models/options';
import { DEFAULT_CANDLE_HEATMAP_OPTIONS, mergeCandleHeatmapOptions } from '../../models/options';
import { clamp } from '../../utils/math';
import type { OhlcLike } from '../../utils/mintick';
import { withAlpha } from '../../utils/color';

const DEFAULT_SURFACE_BACKGROUND = '#020617';
const LIGHT_THEME_MIN_ALPHA = 0.24;
const DARK_THEME_MIN_ALPHA = 0.38;
const CONTINUOUS_SHADE_STEPS = 256;
const LIGHT_THEME_NEUTRAL_TARGET = '#0f172a';
const DARK_THEME_NEUTRAL_TARGET = '#e2e8f0';
const LIGHT_THEME_NEUTRAL_MIX = 0.08;
const DARK_THEME_NEUTRAL_MIX = 0.2;

interface RgbaColor {
  r: number;
  g: number;
  b: number;
  a: number;
}

export interface CandleHeatmapBarLike extends OhlcLike {
  time: TimeValue;
}

export type CandleHeatmapSide = 'min' | 'max';

export interface CandleHeatmapColorResolution {
  color: string;
  side: CandleHeatmapSide;
  intensity: number;
}

export interface ResolveCandleHeatmapColorInput {
  value: number | null | undefined;
  options?: CandleHeatmapPartialOptions;
  backgroundColor?: string;
}

export interface BuildCandleHeatmapSeriesDataInput<TBar extends CandleHeatmapBarLike> {
  bars: readonly TBar[];
  options?: CandleHeatmapPartialOptions;
  backgroundColor?: string;
  getValue: (bar: TBar, index: number, bars: readonly TBar[]) => number | null | undefined;
}

const RGB_REGEX = /^rgba?\(([^)]+)\)$/i;

function parseHexColor(color: string): RgbaColor | null {
  const normalized = color.replace('#', '').trim();

  if (normalized.length === 3) {
    const [r, g, b] = normalized.split('');

    return {
      r: Number.parseInt(`${r}${r}`, 16),
      g: Number.parseInt(`${g}${g}`, 16),
      b: Number.parseInt(`${b}${b}`, 16),
      a: 1,
    };
  }

  if (normalized.length === 4) {
    const [r, g, b, a] = normalized.split('');

    return {
      r: Number.parseInt(`${r}${r}`, 16),
      g: Number.parseInt(`${g}${g}`, 16),
      b: Number.parseInt(`${b}${b}`, 16),
      a: Number.parseInt(`${a}${a}`, 16) / 255,
    };
  }

  if (normalized.length === 6) {
    return {
      r: Number.parseInt(normalized.slice(0, 2), 16),
      g: Number.parseInt(normalized.slice(2, 4), 16),
      b: Number.parseInt(normalized.slice(4, 6), 16),
      a: 1,
    };
  }

  if (normalized.length === 8) {
    return {
      r: Number.parseInt(normalized.slice(0, 2), 16),
      g: Number.parseInt(normalized.slice(2, 4), 16),
      b: Number.parseInt(normalized.slice(4, 6), 16),
      a: Number.parseInt(normalized.slice(6, 8), 16) / 255,
    };
  }

  return null;
}

function parseRgbColor(color: string): RgbaColor | null {
  const match = color.match(RGB_REGEX);

  if (!match) {
    return null;
  }

  const parts = match[1]?.split(',').map((part) => part.trim()) ?? [];

  if (parts.length < 3) {
    return null;
  }

  return {
    r: clamp(Number.parseFloat(parts[0] ?? '0'), 0, 255),
    g: clamp(Number.parseFloat(parts[1] ?? '0'), 0, 255),
    b: clamp(Number.parseFloat(parts[2] ?? '0'), 0, 255),
    a: clamp(Number.parseFloat(parts[3] ?? '1'), 0, 1),
  };
}

function parseColor(color: string): RgbaColor | null {
  if (color.trim().toLowerCase() === 'transparent') {
    return {
      r: 0,
      g: 0,
      b: 0,
      a: 0,
    };
  }

  if (color.startsWith('#')) {
    return parseHexColor(color);
  }

  if (color.startsWith('rgb')) {
    return parseRgbColor(color);
  }

  return null;
}

function toRgbaString(color: RgbaColor): string {
  return `rgba(${Math.round(clamp(color.r, 0, 255))}, ${Math.round(clamp(color.g, 0, 255))}, ${Math.round(clamp(color.b, 0, 255))}, ${clamp(
    color.a,
    0,
    1,
  )
    .toFixed(3)
    .replace(/\.?0+$/, '')})`;
}

function mixRgbColors(from: string, to: string, factor: number): string {
  const start = parseColor(from);
  const end = parseColor(to);

  if (!start || !end) {
    return factor < 0.5 ? from : to;
  }

  const t = clamp(factor, 0, 1);

  return toRgbaString({
    r: start.r + (end.r - start.r) * t,
    g: start.g + (end.g - start.g) * t,
    b: start.b + (end.b - start.b) * t,
    a: 1,
  });
}

function rgbToHsl(color: RgbaColor): { h: number; s: number; l: number } {
  const r = color.r / 255;
  const g = color.g / 255;
  const b = color.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  const delta = max - min;

  if (delta === 0) {
    return { h: 0, s: 0, l: lightness };
  }

  const saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);

  let hue = 0;

  switch (max) {
    case r:
      hue = (g - b) / delta + (g < b ? 6 : 0);
      break;
    case g:
      hue = (b - r) / delta + 2;
      break;
    default:
      hue = (r - g) / delta + 4;
      break;
  }

  return {
    h: hue * 60,
    s: saturation,
    l: lightness,
  };
}

function hueToRgb(p: number, q: number, t: number): number {
  let value = t;

  if (value < 0) {
    value += 1;
  }
  if (value > 1) {
    value -= 1;
  }
  if (value < 1 / 6) {
    return p + (q - p) * 6 * value;
  }
  if (value < 1 / 2) {
    return q;
  }
  if (value < 2 / 3) {
    return p + (q - p) * (2 / 3 - value) * 6;
  }
  return p;
}

function hslToRgba(h: number, s: number, l: number): RgbaColor {
  if (s === 0) {
    const channel = l * 255;

    return {
      r: channel,
      g: channel,
      b: channel,
      a: 1,
    };
  }

  const normalizedHue = (((h % 360) + 360) % 360) / 360;
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  return {
    r: hueToRgb(p, q, normalizedHue + 1 / 3) * 255,
    g: hueToRgb(p, q, normalizedHue) * 255,
    b: hueToRgb(p, q, normalizedHue - 1 / 3) * 255,
    a: 1,
  };
}

function mixHslColors(from: string, to: string, factor: number): string {
  const start = parseColor(from);
  const end = parseColor(to);

  if (!start || !end) {
    return factor < 0.5 ? from : to;
  }

  const left = rgbToHsl(start);
  const right = rgbToHsl(end);
  const t = clamp(factor, 0, 1);
  const hueDelta = ((((right.h - left.h) % 360) + 540) % 360) - 180;
  const mixed = hslToRgba(
    left.h + hueDelta * t,
    left.s + (right.s - left.s) * t,
    left.l + (right.l - left.l) * t,
  );

  return toRgbaString(mixed);
}

function relativeLuminance(color: RgbaColor): number {
  const channels = [color.r, color.g, color.b].map((value) => {
    const normalized = value / 255;
    return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function isDarkSurface(color: string): boolean {
  const parsed = parseColor(color);
  return parsed ? relativeLuminance(parsed) < 0.4 : true;
}

function normalizeNumberOfShades(noOfShades: number): number {
  if (!Number.isFinite(noOfShades) || noOfShades < 0) {
    return 1;
  }

  return Math.floor(noOfShades);
}

function quantizeIntensity(normalizedDistance: number, noOfShades: number): number {
  const clampedDistance = clamp(normalizedDistance, 0, 1);
  const steps = noOfShades === 0 ? CONTINUOUS_SHADE_STEPS : Math.max(noOfShades, 1);

  if (steps === 1) {
    return 1;
  }

  const shadeIndex = Math.min(steps, Math.floor(clampedDistance * steps) + 1);
  return shadeIndex / steps;
}

function resolveNeutralHueBase(backgroundColor: string, darkSurface: boolean): string {
  return mixRgbColors(
    backgroundColor,
    darkSurface ? DARK_THEME_NEUTRAL_TARGET : LIGHT_THEME_NEUTRAL_TARGET,
    darkSurface ? DARK_THEME_NEUTRAL_MIX : LIGHT_THEME_NEUTRAL_MIX,
  );
}

function resolveColorFromShade(
  color: string,
  intensity: number,
  shader: CandleHeatmapShader,
  backgroundColor: string,
  darkSurface: boolean,
): string {
  const parsed = parseColor(color);

  if (!parsed) {
    return color;
  }

  if (shader === 'alpha') {
    const floorRatio = darkSurface ? DARK_THEME_MIN_ALPHA : LIGHT_THEME_MIN_ALPHA;
    const alpha = parsed.a * (floorRatio + (1 - floorRatio) * clamp(intensity, 0, 1));
    return withAlpha(color, alpha);
  }

  return withAlpha(
    mixHslColors(
      resolveNeutralHueBase(backgroundColor, darkSurface),
      withAlpha(color, 1),
      clamp(intensity, 0, 1),
    ),
    parsed.a,
  );
}

interface ResolvedCandleHeatmapOptions {
  range: {
    min: number;
    minShadeThreshold: number;
    threshold: number;
    maxShadeThreshold: number;
    max: number;
  };
  downColor: string;
  upColor: string;
  wickDownColor?: string;
  wickUpColor?: string;
  borderDownColor?: string;
  borderUpColor?: string;
  borderVisible: boolean;
  shadeWicks: boolean;
  noOfShades: number;
  shader: CandleHeatmapShader;
}

function resolveFiniteNumber(value: number | undefined, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function normalizeResolvedOptions(
  options: CandleHeatmapOptions,
): ResolvedCandleHeatmapOptions | null {
  const range: CandleHeatmapRangeOptions = {
    min: resolveFiniteNumber(options.range.min, DEFAULT_CANDLE_HEATMAP_OPTIONS.range.min),
    minShadeThreshold: Number.isFinite(options.range.minShadeThreshold)
      ? options.range.minShadeThreshold
      : undefined,
    threshold: resolveFiniteNumber(
      options.range.threshold,
      DEFAULT_CANDLE_HEATMAP_OPTIONS.range.threshold,
    ),
    maxShadeThreshold: Number.isFinite(options.range.maxShadeThreshold)
      ? options.range.maxShadeThreshold
      : undefined,
    max: resolveFiniteNumber(options.range.max, DEFAULT_CANDLE_HEATMAP_OPTIONS.range.max),
  };

  if (!(range.min < range.threshold && range.threshold < range.max)) {
    return null;
  }

  return {
    range: {
      min: range.min,
      minShadeThreshold: clamp(range.minShadeThreshold ?? range.min, range.min, range.threshold),
      threshold: range.threshold,
      maxShadeThreshold: clamp(range.maxShadeThreshold ?? range.max, range.threshold, range.max),
      max: range.max,
    },
    downColor: options.downColor,
    upColor: options.upColor,
    wickDownColor: options.wickDownColor,
    wickUpColor: options.wickUpColor,
    borderDownColor: options.borderDownColor,
    borderUpColor: options.borderUpColor,
    borderVisible: options.borderVisible,
    shadeWicks: options.shadeWicks,
    noOfShades: normalizeNumberOfShades(options.noOfShades),
    shader: options.shader,
  };
}

function resolveSideDistance(
  value: number,
  options: ResolvedCandleHeatmapOptions,
): { side: CandleHeatmapSide; normalizedDistance: number } {
  const { min, minShadeThreshold, threshold, maxShadeThreshold, max } = options.range;
  const clampedValue = clamp(value, min, max);

  if (clampedValue <= min) {
    return {
      side: 'min',
      normalizedDistance: 1,
    };
  }

  if (clampedValue >= max) {
    return {
      side: 'max',
      normalizedDistance: 1,
    };
  }

  if (options.noOfShades === 1) {
    return {
      side: clampedValue < threshold ? 'min' : 'max',
      normalizedDistance: 1,
    };
  }

  if (clampedValue < threshold) {
    if (minShadeThreshold >= threshold || clampedValue <= minShadeThreshold) {
      return {
        side: 'min',
        normalizedDistance: 1,
      };
    }

    const span = threshold - minShadeThreshold;

    return {
      side: 'min',
      normalizedDistance:
        span <= 0 ? 1 : clamp(1 - (clampedValue - minShadeThreshold) / span, 0, 1),
    };
  }

  if (maxShadeThreshold <= threshold || clampedValue >= maxShadeThreshold) {
    return {
      side: 'max',
      normalizedDistance: 1,
    };
  }

  const span = maxShadeThreshold - threshold;

  return {
    side: 'max',
    normalizedDistance: span <= 0 ? 1 : clamp((clampedValue - threshold) / span, 0, 1),
  };
}

function resolveHeatmapColor(
  side: CandleHeatmapSide,
  intensity: number,
  options: ResolvedCandleHeatmapOptions,
  backgroundColor: string,
  darkSurface: boolean,
): string {
  const edgeColor = side === 'min' ? options.downColor : options.upColor;

  if (intensity >= 1) {
    return edgeColor;
  }

  return resolveColorFromShade(edgeColor, intensity, options.shader, backgroundColor, darkSurface);
}

function resolveSideFillColor(
  side: CandleHeatmapSide,
  options: ResolvedCandleHeatmapOptions,
): string {
  return side === 'min' ? options.downColor : options.upColor;
}

function resolveSideWickColor(
  side: CandleHeatmapSide,
  options: ResolvedCandleHeatmapOptions,
): string {
  return side === 'min'
    ? (options.wickDownColor ?? options.downColor)
    : (options.wickUpColor ?? options.upColor);
}

function resolveSideBorderColor(
  side: CandleHeatmapSide,
  options: ResolvedCandleHeatmapOptions,
): string {
  return side === 'min'
    ? (options.borderDownColor ?? options.downColor)
    : (options.borderUpColor ?? options.upColor);
}

function resolveShadedSideColor(
  baseColor: string,
  intensity: number,
  options: ResolvedCandleHeatmapOptions,
  backgroundColor: string,
  darkSurface: boolean,
): string {
  if (intensity >= 1) {
    return baseColor;
  }

  return resolveColorFromShade(baseColor, intensity, options.shader, backgroundColor, darkSurface);
}

export function resolveCandleHeatmapColor(
  input: ResolveCandleHeatmapColorInput,
): CandleHeatmapColorResolution | null {
  if (!Number.isFinite(input.value)) {
    return null;
  }

  const options = normalizeResolvedOptions(mergeCandleHeatmapOptions(input.options));

  if (!options) {
    return null;
  }

  const backgroundColor = input.backgroundColor ?? DEFAULT_SURFACE_BACKGROUND;
  const darkSurface = isDarkSurface(backgroundColor);
  const { side, normalizedDistance } = resolveSideDistance(input.value as number, options);
  const intensity = quantizeIntensity(normalizedDistance, options.noOfShades);

  return {
    color: resolveHeatmapColor(side, intensity, options, backgroundColor, darkSurface),
    side,
    intensity,
  };
}

export function buildCandleHeatmapSeriesData<TBar extends CandleHeatmapBarLike>(
  input: BuildCandleHeatmapSeriesDataInput<TBar>,
): CandlestickData<TimeValue>[] {
  const options = normalizeResolvedOptions(mergeCandleHeatmapOptions(input.options));
  const backgroundColor = input.backgroundColor ?? DEFAULT_SURFACE_BACKGROUND;
  const darkSurface = isDarkSurface(backgroundColor);

  return input.bars.map((bar, index, bars) => {
    if (!options) {
      return {
        time: bar.time,
        open: bar.open,
        high: bar.high,
        low: bar.low,
        close: bar.close,
      };
    }

    const resolution = resolveCandleHeatmapColor({
      value: input.getValue(bar, index, bars),
      options,
      backgroundColor,
    });
    const fillColor = resolution
      ? resolveShadedSideColor(
          resolveSideFillColor(resolution.side, options),
          resolution.intensity,
          options,
          backgroundColor,
          darkSurface,
        )
      : undefined;
    const wickColor = resolution
      ? options.shadeWicks
        ? resolveShadedSideColor(
            resolveSideWickColor(resolution.side, options),
            resolution.intensity,
            options,
            backgroundColor,
            darkSurface,
          )
        : resolveSideWickColor(resolution.side, options)
      : undefined;
    const borderColor = resolution
      ? options.borderVisible
        ? resolveShadedSideColor(
            resolveSideBorderColor(resolution.side, options),
            resolution.intensity,
            options,
            backgroundColor,
            darkSurface,
          )
        : 'transparent'
      : undefined;

    return {
      time: bar.time,
      open: bar.open,
      high: bar.high,
      low: bar.low,
      close: bar.close,
      ...(fillColor
        ? {
            color: fillColor,
            borderColor: borderColor ?? fillColor,
            wickColor: wickColor ?? fillColor,
          }
        : {}),
    };
  });
}
